import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { initializeApp, getApps } from 'firebase/app';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import './PublicLinksPage.css';

const publicFirebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_apiKey,
  authDomain: process.env.REACT_APP_FIREBASE_authDomain,
  projectId: process.env.REACT_APP_FIREBASE_projectId,
  storageBucket: process.env.REACT_APP_FIREBASE_storageBucket,
  messagingSenderId: process.env.REACT_APP_FIREBASE_messagingSenderId,
  appId: process.env.REACT_APP_FIREBASE_appId,
  measurementId: process.env.REACT_APP_FIREBASE_measurementId
};

const existingPublicApp = getApps().find(app => app.name === 'public-app');
const publicApp = existingPublicApp || initializeApp(publicFirebaseConfig, 'public-app');
const publicDb = getFirestore(publicApp);

// Extended LinkItem - new fields are all optional for backward compatibility
interface LinkItem {
  id?: string;
  name: string;
  url?: string;
  thumbnailUrl?: string;
  nonExternalMode?: boolean;
  description?: string;
  availability?: string;
  appointmentsRequired?: boolean;
  promotions?: string;
  otherInfo?: string;
}

interface LinkGroup {
  groupName: string;
  links: LinkItem[];
}

interface IconConfig {
  type: 'links';
  groups: LinkGroup[];
}

interface IconLinkData {
  iconConfig: IconConfig;
  externalSite?: string;
  iconName: string;
  enabled: boolean;
}

interface LinksDocument {
  data: Record<string, IconLinkData>;
  lastUpdated: any;
  conferenceId: string;
}

const PublicLinksPage: React.FC = () => {
  const { conferenceId, iconId, itemId } = useParams<{
    conferenceId: string;
    iconId: string;
    itemId?: string;
  }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [iconData, setIconData] = useState<IconLinkData | null>(null);

  useEffect(() => {
    const loadLinksData = async () => {
      if (!conferenceId || !iconId) {
        setError('Invalid URL parameters');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const linksRef = doc(publicDb, 'links', conferenceId);
        const linksSnapshot = await getDoc(linksRef);

        if (!linksSnapshot.exists()) {
          setError('No links found for this conference');
          setLoading(false);
          return;
        }

        const linksData = linksSnapshot.data() as LinksDocument;

        if (!linksData.data || !linksData.data[iconId]) {
          setError('No links found for this icon');
          setLoading(false);
          return;
        }

        const iconLinkData = linksData.data[iconId];

        if (!iconLinkData.enabled) {
          setError('This content is currently unavailable');
          setLoading(false);
          return;
        }

        setIconData(iconLinkData);
        setLoading(false);

      } catch (err) {
        console.error('Error loading links data:', err);
        setError('Failed to load content. Please try again later.');
        setLoading(false);
      }
    };

    loadLinksData();
  }, [conferenceId, iconId]);

  // Get a stable identifier for a link - uses id field if present, falls back to index
  const getLinkIdentifier = (link: LinkItem, index: number): string => {
    return link.id || String(index);
  };

  // Resolve a link from the itemId param - tries id field first, falls back to index
  const resolveItemFromId = (itemId: string): LinkItem | null => {
    if (!iconData) return null;

    for (const group of iconData.iconConfig.groups) {
      for (let i = 0; i < group.links.length; i++) {
        const link = group.links[i];
        const identifier = link.id || String(i);
        if (identifier === itemId) {
          return link;
        }
      }
    }
    return null;
  };

  // Detects URLs in a string and renders them as clickable anchor tags
  const linkifyText = (text: string): React.ReactNode => {
    const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;
    const parts = text.split(urlRegex);
    return parts.map((part, i) => {
      if (urlRegex.test(part)) {
        const href = part.startsWith('http') ? part : `https://${part}`;
        return (
          <a
            key={i}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="public-links-inline-link"
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  const handleLinkClick = (link: LinkItem, index: number) => {
    if (link.nonExternalMode) {
      const identifier = getLinkIdentifier(link, index);
      navigate(`/${conferenceId}/${iconId}/${identifier}`);
    } else {
      const url = link.url || '';
      const fullUrl = url.startsWith('http://') || url.startsWith('https://')
        ? url
        : `https://${url}`;
      window.open(fullUrl, '_blank', 'noopener,noreferrer');
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="public-links-page">
        <div className="public-links-loading">
          <div className="public-links-spinner"></div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="public-links-page">
        <div className="public-links-header">
          <h1>Content Not Found</h1>
        </div>
        <div className="public-links-container">
          <div className="public-links-section">
            <div className="public-links-section-content public-links-error-content">
              <p>{error}</p>
              <button
                onClick={() => navigate('/')}
                className="public-links-back-btn"
              >
                Go to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!iconData) {
    return (
      <div className="public-links-page">
        <div className="public-links-header">
          <h1>No Content Available</h1>
        </div>
      </div>
    );
  }

  // Detail view - rendered when itemId param is present
  if (itemId) {
    const item = resolveItemFromId(itemId);

    if (!item) {
      return (
        <div className="public-links-page">
          <div className="public-links-header">
            <h1>Not Found</h1>
          </div>
          <div className="public-links-container">
            <div className="public-links-section">
              <div className="public-links-section-content public-links-error-content">
                <p>This item could not be found.</p>
                <button
                  onClick={() => navigate(`/${conferenceId}/${iconId}`)}
                  className="public-links-back-btn"
                >
                  ← Back
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="public-links-page">
        <div className="public-links-header">
          {item.thumbnailUrl && (
            <div className="public-links-detail-logo">
              <img src={item.thumbnailUrl} alt={item.name} />
            </div>
          )}
          <h1>{item.name}</h1>
        </div>

        <div className="public-links-container">
          <button
            onClick={() => navigate(`/${conferenceId}/${iconId}`)}
            className="public-links-back-btn public-links-back-btn--top"
          >
            ← Back
          </button>

          {item.description && (
            <div className="public-links-section">
              <div className="public-links-section-header">About</div>
              <div className="public-links-section-content">
                <p className="public-links-detail-text">{linkifyText(item.description)}</p>
              </div>
            </div>
          )}

          {item.availability && (
            <div className="public-links-section">
              <div className="public-links-section-header">Availability</div>
              <div className="public-links-section-content">
                <p className="public-links-detail-text">{linkifyText(item.availability)}</p>
              </div>
            </div>
          )}

          {item.promotions && (
            <div className="public-links-section">
              <div className="public-links-section-header">Promotions</div>
              <div className="public-links-section-content">
                <p className="public-links-detail-text">{linkifyText(item.promotions)}</p>
              </div>
            </div>
          )}

          {item.otherInfo && (
            <div className="public-links-section">
              <div className="public-links-section-header">Other Information</div>
              <div className="public-links-section-content">
                <p className="public-links-detail-text">{linkifyText(item.otherInfo)}</p>
              </div>
            </div>
          )}

          {item.appointmentsRequired !== undefined && (
            <div className="public-links-section">
              <div className="public-links-section-header">Appointments</div>
              <div className="public-links-section-content">
                <p className="public-links-detail-text">
                  {item.appointmentsRequired
                    ? 'Appointments are required. Please visit the stand to book.'
                    : 'No appointment needed — just drop by the stand.'}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // List view - default, backward compatible
  return (
    <div className="public-links-page">
      <div className="public-links-container">
        {iconData.iconConfig.groups.map((group, groupIndex) => {
          // A group uses tile layout if any link has nonExternalMode or thumbnailUrl
          const groupHasTiles = group.links.some(
            link => link.nonExternalMode || link.thumbnailUrl
          );

          return (
            <section key={groupIndex} className="public-links-section">
              {group.groupName && (
                <div className="public-links-section-header">
                  {group.groupName}
                </div>
              )}
              <div className="public-links-section-content">
                {groupHasTiles ? (
                  // Tile grid layout for nonExternalMode / thumbnail links
                  <div className="public-links-tile-grid">
                    {group.links.map((link, linkIndex) => (
                      <div
                        key={linkIndex}
                        className="public-links-tile"
                        onClick={() => handleLinkClick(link, linkIndex)}
                      >
                        <div className="public-links-tile-image">
                          {link.thumbnailUrl ? (
                            <img src={link.thumbnailUrl} alt={link.name} />
                          ) : (
                            <div className="public-links-tile-placeholder">
                              {link.name.charAt(0).toUpperCase()}
                            </div>
                          )}
                        </div>
                        <span className="public-links-tile-name">{link.name}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  // Original list layout - unchanged for backward compatibility
                  <div>
                    {group.links.map((link, linkIndex) => (
                      <a
                        key={linkIndex}
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handleLinkClick(link, linkIndex);
                        }}
                        className="public-links-link-item"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {link.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
};

export default PublicLinksPage;